// Added by Sejune Oh on 2019-08-27
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import axios from 'axios'
import crypto from 'crypto-browserify'
import { AUTH_TOKEN_COOKIE, HTTP_HEADER_AUTH_KEY, ACCESS_TOKEN } from '@/helper/define'
import { setCookie } from '@/helper/util'
import { DisplableUserElementProps } from '../../mgmt/user'
import { AuthTokenProps } from './UserAuth'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ReqAuthenticateProps {
  auth_id: string
  password: string
  check_only?: boolean
  keep_me_logged_in?: boolean
}

interface ResAuthenticateProps {
  access_token: string
  refresh_token: string
  max_login_attempts: number
  failed_count: number
}

export const reqAuthenticate = async ({
  auth_id,
  password,
  check_only = false,
  keep_me_logged_in = false,
}: ReqAuthenticateProps) => {
  // Get RSA public key from platform
  const { data } = await axios.get('v1/auth/rsa-public-key')
  const rsa_public_key = data.public_key
  // Encrypt user information if the RSA public key is valid
  if (rsa_public_key) {
    const encrypt = (v: string) => {
      return crypto.publicEncrypt(rsa_public_key, Buffer.from(v, 'utf8')).toString('base64')
    }
    auth_id = encrypt(auth_id)
    password = encrypt(password)
  }

  return await axios.post<ResAuthenticateProps>(`v1/auth/authenticate`, {
    auth_id,
    password,
    check_only,
    keep_me_logged_in,
  })
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ReqAuthenticateAccessTokenProps {
  access_token: string
}

interface ResAuthenticateAccessTokenProps {
  access_token: string
  refresh_token: string
}

export const reqAuthenticateAccessToken = async ({ access_token }: ReqAuthenticateAccessTokenProps) => {
  return await axios.post<ResAuthenticateAccessTokenProps>(`v1/auth/authenticate/access-token`, {
    access_token,
  })
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ReqAuthenticateLinkTokenProps {
  link_token: string
}

interface ResAuthenticatesLinkTokenProps {
  access_token?: string
  refresh_token?: string
}

export const reqAuthenticateLinkToken = async ({ link_token }: ReqAuthenticateLinkTokenProps) => {
  return await axios.post<ResAuthenticatesLinkTokenProps>(`v1/auth/authenticate/link-token`, {
    link_token,
  })
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ReqCreateSysAdminProps {
  username: string
  display_name: string
  email: string
  password: string
  description: string
  ui_language: string
}

interface ResCreateSysAdminProps {}

export const reqCreateSysAdmin = async ({
  username,
  display_name,
  email,
  password,
  description,
  ui_language,
}: ReqCreateSysAdminProps) => {
  return await axios.post<ResCreateSysAdminProps>('v1/auth/create-sys-admin', {
    username,
    display_name,
    email,
    password,
    description,
    ui_language,
  })
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ReqGetAuthenticatedUserInfo {
  username: string
}

export const reqGetAuthenticatedUserInfo = async ({ username }: ReqGetAuthenticatedUserInfo) => {
  return await axios.get<DisplableUserElementProps>(`v1/auth/user-info/${encodeURIComponent(username)}`)
}


interface ReqGetTwoFactorAuthData {
  auth2_data: string
}
export const reqTwoFactorData = async (id: string) => {
  return await axios.post<ReqGetTwoFactorAuthData>(`v1/mgmt/users/${id}/auth2`, {
    auth2_type: 'OTP',
  })
}

interface ReqPostTwoFactorRegister {
  success: boolean
}
export const reqPostTwoFactorRegister = async (id: string, auth2_data:string) => {
  return await axios.post<ReqPostTwoFactorRegister>(`v1/mgmt/users/${id}/auth2:register`, {
    auth2_type: 'OTP',
    auth2_data: auth2_data,
  })
}

interface ReqPostTwoFactorAuth {
  success: boolean
}
export const reqPostTwoFactorAuth = async (id: string, auth2_data:string) => {
  return await axios.post<ReqPostTwoFactorAuth>(`v1/mgmt/users/${id}/auth2:authenticate`, {
    auth2_type: 'OTP',
    auth2_data: auth2_data,
  })
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ResRefreshProps {
  access_token: string
  refresh_token: string
}

export const reqRefresh = async (authToken: AuthTokenProps) => {
  return await axios.post<ResRefreshProps>('v1/auth/refresh', {
    ...authToken,
  })
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const setTokenCookieAndHeaderAuthProp = (authToken: AuthTokenProps) => {
  setCookie({ name: AUTH_TOKEN_COOKIE, value: JSON.stringify(authToken), expHours: 24 })
  setCookie({ name: ACCESS_TOKEN, value: `${authToken!.access_token}`, expHours: 24 })
  axios.defaults.headers.common[HTTP_HEADER_AUTH_KEY] = `Bearer ${authToken!.access_token}`
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const reqRevokeAccessToken = async ({ access_token }: ReqAuthenticateAccessTokenProps) => {
  return await axios.post<ResAuthenticatesLinkTokenProps>(`v1/auth/revoke-access-token`, {
    access_token,
  })
}